import React from 'react';
import { Card } from '@/components/ui/card';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export default function AuthBreakdownCard({ authBreakdown }) {
  return (
    <div className="mb-8">
      <h2 className="text-xl font-bold mb-4 text-muted-foreground uppercase">Customer Signup Methods</h2>
      <Card className="p-6" data-testid="auth-breakdown-card">
        {!authBreakdown ? (
          <div className="text-center py-12 text-muted-foreground text-sm">Loading...</div>
        ) : authBreakdown.total === 0 ? (
          <div className="text-center py-12 text-muted-foreground text-sm">No registered customers yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">

            {/* Donut chart */}
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Email & Password', value: authBreakdown.password },
                      { name: 'Google', value: authBreakdown.google },
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={110}
                    paddingAngle={authBreakdown.google > 0 && authBreakdown.password > 0 ? 3 : 0}
                    dataKey="value"
                    startAngle={90}
                    endAngle={-270}
                  >
                    <Cell fill="#047857" />
                    <Cell fill="#4285F4" />
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const { name, value } = payload[0];
                      const pct = authBreakdown.total
                        ? Math.round((value / authBreakdown.total) * 100)
                        : 0;
                      return (
                        <div className="bg-background border rounded-lg px-3 py-2 shadow-lg text-sm">
                          <p className="font-bold mb-0.5">{name}</p>
                          <p className="text-muted-foreground">{value} users &middot; {pct}%</p>
                        </div>
                      );
                    }}
                  />
                  <Legend
                    iconType="circle"
                    iconSize={10}
                    formatter={(value) => (
                      <span className="text-sm text-foreground">{value}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Stat summary */}
            <div className="space-y-5" data-testid="auth-breakdown-stats">
              <div>
                <p className="text-xs uppercase font-bold text-muted-foreground tracking-wide mb-3">
                  Total Registered Users
                </p>
                <p className="text-5xl font-black font-heading">{authBreakdown.total}</p>
              </div>

              <div className="space-y-3 pt-2 border-t">
                {/* Email & Password row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-emerald-700 shrink-0" />
                    <span className="text-sm font-medium">Email &amp; Password</span>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-black font-heading">{authBreakdown.password}</span>
                    <span className="text-xs text-muted-foreground ml-1.5">
                      ({authBreakdown.total ? Math.round((authBreakdown.password / authBreakdown.total) * 100) : 0}%)
                    </span>
                  </div>
                </div>

                {/* Google row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: '#4285F4' }} />
                    <span className="flex items-center gap-1.5 text-sm font-medium">
                      <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      Google
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-black font-heading">{authBreakdown.google}</span>
                    <span className="text-xs text-muted-foreground ml-1.5">
                      ({authBreakdown.total ? Math.round((authBreakdown.google / authBreakdown.total) * 100) : 0}%)
                    </span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        )}
      </Card>
    </div>
  );
}
